import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../API';
import RoleBadge from './roleUtils';
import ProjectsPageShell from './hub/ProjectsPageShell';
import ProjectBrowseKindIcon from './hub/ProjectBrowseKindIcon';
import { IconExternal, IconSettings } from './hub/HubRowActionIcons';
import HubRowPrimaryTrailIcon from './hub/HubRowPrimaryTrailIcon';
import HubProjectStatusChip from './hub/HubProjectStatusChip';
import styles from './ProjectsHome.module.css';

/**
 * ProjectsHome — "My projects" at /projects (assignments from projects/my).
 * Browse and organize lives in the left nav when the user has hierarchy access.
 */
function ProjectsHome() {
    const navigate = useNavigate();
    const [myProjects, setMyProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        setLoading(true);
        setError(null);
        api.get('projects/my', {})
            .then((r) => {
                if (r.status === 200) {
                    setMyProjects(r.data?.projects ?? []);
                } else {
                    setError(r.data?.message || 'Failed to load projects.');
                }
            })
            .catch((e) => setError(e.message || 'Failed to load projects.'))
            .finally(() => setLoading(false));
    }, [retryCount]);

    return (
        <ProjectsPageShell
            title="My projects"
            subtitle="Click to open the project workspace. Admins can use the gear icon to configure the project."
            loading={loading}
            error={error}
            onRetry={error ? () => setRetryCount((c) => c + 1) : undefined}
        >
            {myProjects.length === 0 ? (
                <p className={styles.emptyStatePlain}>
                    No projects are assigned to you yet. If you expected to see a project, ask your
                    administrator to add you as a participant.
                </p>
            ) : (
                <ul className={styles.hubList}>
                    {myProjects.map((proj) => {
                        const roles = proj.myRoles || [];
                        const isAdmin = roles.includes('admin');
                        return (
                            <li key={proj._id} className={`${styles.hubRow} ${styles.hubRowProject}`}>
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className={`${styles.hubRowPrimary} ${styles.hubRowPrimaryProject}`}
                                    onClick={() => navigate(`/projects/${proj._id}/participate`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/projects/${proj._id}/participate`);
                                        }
                                    }}
                                    aria-label={`Open project workspace: ${proj.name}`}
                                    title={`Open project workspace — ${proj.name}`}
                                >
                                    <div className={styles.hubRowMain}>
                                        <div className={styles.hubRowTitleRow}>
                                            <span
                                                className={`${styles.browseKind} ${styles.browseKindProject}`}
                                                title="Project"
                                            >
                                                <ProjectBrowseKindIcon type="project" />
                                                <span className={styles.visuallyHidden}>Project</span>
                                            </span>
                                            <span className={styles.hubRowName}>{proj.name}</span>
                                            <HubProjectStatusChip lifecycleStatus={proj.girderProjects?.lifecycleStatus} />
                                        </div>
                                        {roles.length > 0 ? (
                                            <div className={styles.roleChipList} aria-label="Your roles">
                                                {roles.map((role) => (
                                                    <span key={role} className={styles.roleChip}>
                                                        <RoleBadge role={role} variant="long" />
                                                    </span>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>
                                    <span className={styles.hubRowPrimaryTrail}>
                                        <HubRowPrimaryTrailIcon variant="project" />
                                    </span>
                                </div>
                                <div className={styles.hubRowActions}>
                                    {isAdmin ? (
                                        <button
                                            type="button"
                                            className={styles.hubIconBtn}
                                            title="Project settings — annotation UI, participants, items"
                                            aria-label="Project settings"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/projects/${proj._id}`);
                                            }}
                                        >
                                            <IconSettings />
                                        </button>
                                    ) : null}
                                    <a
                                        href={`/dsa#folder/${proj._id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.hubIconLink}
                                        title="Open in Girder — default DSA / Girder folder UI"
                                        aria-label="Open in Girder"
                                    >
                                        <IconExternal />
                                    </a>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </ProjectsPageShell>
    );
}

export default ProjectsHome;
